import { useCallback } from 'react';
import { Domingo } from '@fiverr-private/domingo';
import pathfinder from '@fiverr-private/pathfinder';
import { getContext } from '@fiverr-private/fiverr_context';
import { SocialProvider } from '../../components/SocialButton/constants';
import windowManager from '../../utils/windowManager';
import { logError } from '../../utils/logger';
import { globalGrafanaReporter } from '../../events/grafana';
import { AuthenticationResponse, AuthenticationPostMessagePayload } from '../../shared/constants/authenticationFlow';
import { AUTHENTICATION_EVENT, BROADCAST_CHANNEL_NAME, WINDOW_NAME } from './constants';

declare global {
    interface WindowEventMap {
        socialAuthenticationMessage: CustomEvent<AuthenticationResponse>;
    }
}

interface UseSocialButtonClickHandlerProps {
    provider: SocialProvider;
    onClick?: () => void;
    onSuccess?: (provider: SocialProvider, authenticationResponse: AuthenticationResponse) => void;
    onError?: (provider: SocialProvider, authenticationResponse: AuthenticationResponse) => void;
}

export const useSocialButtonClickHandler = ({
    provider,
    onClick,
    onSuccess,
    onError,
}: UseSocialButtonClickHandlerProps) => {
    const onSocialButtonClick = useCallback(() => {
        const channel = new BroadcastChannel(BROADCAST_CHANNEL_NAME);

        const handleSocialAuthenticationPostMessage = (
            event: MessageEvent<AuthenticationPostMessagePayload>,
            isBroadcastEvent: boolean
        ) => {
            if (event.data.name !== AUTHENTICATION_EVENT) return;

            const authOrigin = Domingo(window.origin).auth().removePath().url;

            // ! Failure to check the origin property enables cross-site scripting attacks !
            if (!isBroadcastEvent && ![authOrigin, window.origin].includes(event.origin)) {
                globalGrafanaReporter.count('social_signing.%{platform}.post_message_callback.wrong_origin');
                logError(`Received a "${AUTHENTICATION_EVENT}" post message from an unexpected origin`, {
                    origin: event.origin,
                });
                return;
            }

            // ! Failure to check the source property enables cross-site scripting attacks !
            if (!isBroadcastEvent && event.source !== windowManager.current) {
                globalGrafanaReporter.count('social_signing.%{platform}.post_message_callback.wrong_source');
                return;
            }

            const { response: authenticationResponse } = event.data;
            const { result_hash: result } = authenticationResponse;

            window.removeEventListener('message', () => handleSocialAuthenticationPostMessage(event, false));
            channel.close();
            windowManager.close();

            if (result.status === 'success') {
                onSuccess?.(provider, authenticationResponse);
                return;
            }

            if (result.status === 'failure' || result.error) {
                onError?.(provider, authenticationResponse);
            }
        };

        channel.onmessage = (event) => handleSocialAuthenticationPostMessage(event, true);
        window.addEventListener('message', (event) => handleSocialAuthenticationPostMessage(event, false));

        onClick?.();

        const { isAndroid, isMobile } = getContext();
        const androidAuthUrl = Domingo(window.origin).auth().path('auth_redirect', { provider }).url;
        const regularAuthUrl = pathfinder('auth_redirect', { provider });

        const authUrl = isMobile && isAndroid ? androidAuthUrl : regularAuthUrl;

        windowManager.open(authUrl, WINDOW_NAME);
    }, [provider, onClick, onSuccess, onError]);

    return { onSocialButtonClick };
};
